var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery, useQueryClient } from 'react-query';
import { fetchProposals, fetchProposalTabCounts } from '../api/proposalService';
import { DEFAULT_PROPOSAL_OWNERSHIP_FILTER, foldProposalCountsByTab, isProposalOwnershipFilter, TAB_API_PARAMS, WORKFLOW_TABS, } from '../types/api';
import { readOwnershipFilter, writeOwnershipFilter } from '../utils/ownershipFilterPreference';
import useAuthorizationContext from './useAuthorizationContext';
import { mclPaging } from '../utils/mclPaging';
var PAGE_SIZE = 50;
/**
 * Root of every proposals query key.
 *
 * <p>The eight proposal mutations invalidate exactly `[PROPOSALS_QUERY_ROOT]`
 * (useProposalReviewActions, RejectRequestChangesButtons, ProposalDetail, ProposalForm), so
 * anything that must refresh after an approval has to live UNDER this root. Named here so
 * the coupling is visible from the hook rather than being an accident of two matching
 * string literals.
 */
export var PROPOSALS_QUERY_ROOT = 'czna-authorities-proposals';
/**
 * Maps visible column names to backend sortBy parameter values.
 * Only sortable columns are listed here.
 */
var SORT_COLUMN_MAP = {
    headingValue: 'headingValue',
    type: 'recordType',
    date: 'updatedAt',
};
/**
 * Maps hook's sort direction ('ascending'/'descending') to backend API values ('asc'/'desc').
 */
var toApiSortOrder = function (direction) {
    return direction === 'ascending' ? 'asc' : 'desc';
};
/**
 * Validates that a string is a valid WorkflowTab value.
 */
var isValidWorkflowTab = function (value, allowedTabs) {
    if (allowedTabs === void 0) { allowedTabs = WORKFLOW_TABS; }
    return allowedTabs.includes(value);
};
/**
 * Extracts the sort value from a proposal item based on the current sort column.
 * The backend uses this value for keyset cursor positioning.
 */
var getSortValue = function (item, apiSortBy) {
    switch (apiSortBy) {
        case 'headingValue':
            return item.headingValue;
        case 'recordType':
            return item.recordType;
        case 'updatedAt':
        default:
            return item.updatedAt;
    }
};
/**
 * Hook for proposals list page with workflow tabs, text search, filters, sorting, and keyset pagination.
 * Search is triggered explicitly via handleSearch (form submit / Enter key).
 * Tab change resets all filters, query, sort and pagination.
 * Filter state is persisted to URL search params so it survives back-navigation.
 * Pagination uses keyset cursors (afterSortValue/afterId, beforeSortValue/beforeId)
 * while maintaining a numeric pageOffset for MCL display.
 */
var useProposalList = function (_a) {
    var _b, _c, _d, _f;
    var _g = _a === void 0 ? {} : _a, _h = _g.allowedTabs, allowedTabs = _h === void 0 ? WORKFLOW_TABS : _h, _j = _g.defaultTab, defaultTab = _j === void 0 ? (_b = allowedTabs[0]) !== null && _b !== void 0 ? _b : 'DRAFT' : _j, forcedOwnership = _g.forcedOwnership;
    var ky = useOkapiKy();
    var location = useLocation();
    var history = useHistory();
    var _k = useAuthorizationContext(), authorizationContext = _k.context, isAuthorizationLoading = _k.isLoading;
    // Parse initial filter values from URL search params (read once on mount)
    var initialParams = useMemo(function () {
        var params = new URLSearchParams(location.search);
        var tabParam = params.get('tab') || '';
        // Backward compat: ?tab=PENDING_REVIEW → PENDING_NEW
        var resolvedTab = tabParam === 'PENDING_REVIEW' ? 'PENDING_NEW' : tabParam;
        // CZNA-593: an explicit ?ownership= in the URL wins over the remembered preference —
        // a shared or bookmarked link must show what its sender saw.
        var ownershipParam = params.get('ownership');
        return {
            activeTab: isValidWorkflowTab(resolvedTab, allowedTabs) ? resolvedTab : defaultTab,
            heading: params.get('heading') || '',
            proposalType: params.get('proposalType') || null,
            type: params.get('recordType') || null,
            ownership: forcedOwnership !== null && forcedOwnership !== void 0 ? forcedOwnership : (isProposalOwnershipFilter(ownershipParam)
                ? ownershipParam
                : readOwnershipFilter()),
            sort: params.get('sort') || 'date',
            order: params.get('order') || 'descending',
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    var _l = useState(initialParams.activeTab), activeTab = _l[0], setActiveTab = _l[1];
    var _m = useState(initialParams.heading), query = _m[0], setQuery = _m[1];
    var _o = useState(initialParams.heading), appliedQuery = _o[0], setAppliedQuery = _o[1];
    var _p = useState(initialParams.proposalType), proposalTypeFilter = _p[0], setProposalTypeFilter = _p[1];
    var _q = useState(initialParams.proposalType), appliedProposalTypeFilter = _q[0], setAppliedProposalTypeFilter = _q[1];
    var _r = useState(initialParams.type), typeFilter = _r[0], setTypeFilter = _r[1];
    var _s = useState(initialParams.type), appliedTypeFilter = _s[0], setAppliedTypeFilter = _s[1];
    var _t = useState(initialParams.ownership), ownershipFilter = _t[0], setOwnershipFilter = _t[1];
    var _u = useState(initialParams.ownership), appliedOwnershipFilter = _u[0], setAppliedOwnershipFilter = _u[1];
    var _v = useState(initialParams.sort), sortColumn = _v[0], setSortColumn = _v[1];
    var _w = useState(initialParams.order), sortDirection = _w[0], setSortDirection = _w[1];
    var _x = useState(0), pageOffset = _x[0], setPageOffset = _x[1];
    var _y = useState({}), keysetState = _y[0], setKeysetState = _y[1];
    var _z = useState(null), countForResultSet = _z[0], setCountForResultSet = _z[1];
    var _0 = useState(true), filterPaneIsVisible = _0[0], setFilterPaneIsVisible = _0[1];
    /**
     * CZNA-593: has the user actually operated the ownership control in this session?
     *
     * <p>Only then may a search write the preference. Without this, opening a colleague's
     * `?ownership=ORGANIZATION` link and then doing something unrelated — typing a heading
     * and pressing Enter — would overwrite the recipient's own remembered choice, and they
     * would silently inherit the sender's filter from the next session onwards. The URL is
     * meant to override the view, not the preference.
     */
    var ownershipTouched = React.useRef(false);
    var handleSearch = useCallback(function (e) {
        e.preventDefault();
        setAppliedQuery(query);
        setAppliedProposalTypeFilter(proposalTypeFilter);
        setAppliedTypeFilter(typeFilter);
        setAppliedOwnershipFilter(ownershipFilter);
        if (ownershipTouched.current) {
            writeOwnershipFilter(ownershipFilter);
        }
        setPageOffset(0);
        setKeysetState({});
    }, [query, proposalTypeFilter, typeFilter, ownershipFilter]);
    var handleTabChange = useCallback(function (tab) {
        if (!allowedTabs.includes(tab))
            return;
        setActiveTab(tab);
        setQuery('');
        setAppliedQuery('');
        setProposalTypeFilter(null);
        setAppliedProposalTypeFilter(null);
        setTypeFilter(null);
        setAppliedTypeFilter(null);
        // CZNA-593: ownership deliberately SURVIVES a tab change, unlike the filters above.
        // It is a remembered preference ("show me my own work"), and a preference that is
        // remembered across sessions but wiped by clicking a tab would be incoherent —
        // "Jen moje" would silently become "vše" the moment the user looks at another queue.
        // "Zrušit filtry" still clears it.
        setSortColumn('date');
        setSortDirection('descending');
        setPageOffset(0);
        setKeysetState({});
    }, [allowedTabs]);
    // Sync applied filter state to URL search params so filters survive back-navigation
    useEffect(function () {
        var params = new URLSearchParams();
        if (activeTab !== defaultTab)
            params.set('tab', activeTab);
        if (appliedQuery)
            params.set('heading', appliedQuery);
        if (appliedProposalTypeFilter)
            params.set('proposalType', appliedProposalTypeFilter);
        if (appliedTypeFilter)
            params.set('recordType', appliedTypeFilter);
        if (!forcedOwnership && appliedOwnershipFilter !== DEFAULT_PROPOSAL_OWNERSHIP_FILTER) {
            params.set('ownership', appliedOwnershipFilter);
        }
        if (sortColumn !== 'date')
            params.set('sort', sortColumn);
        if (sortDirection !== 'descending')
            params.set('order', sortDirection);
        var search = params.toString();
        history.replace({
            search: search ? "?".concat(search) : '',
        });
    }, [activeTab, appliedQuery, appliedProposalTypeFilter, appliedTypeFilter, appliedOwnershipFilter, sortColumn, sortDirection, defaultTab, forcedOwnership, history]); // eslint-disable-line react-hooks/exhaustive-deps
    var userId = authorizationContext === null || authorizationContext === void 0 ? void 0 : authorizationContext.userId;
    var userOrganizationId = (_c = authorizationContext === null || authorizationContext === void 0 ? void 0 : authorizationContext.organizationId) !== null && _c !== void 0 ? _c : null;
    /**
     * CZNA-593 — "Moje instituce" is only offered to a user who belongs to one. A user with
     * no organization has nothing the filter could mean, and offering it would produce an
     * empty list that looks like a bug.
     */
    var canFilterByOrganization = !!userOrganizationId;
    /**
     * CZNA-593: a remembered (or link-supplied) ORGANIZATION choice that this user cannot use
     * is cleared out of the state, not merely ignored when building the request.
     *
     * <p>Ignoring it alone left the UI asserting something untrue: the Select is rendered
     * without that option, so a controlled value of 'ORGANIZATION' matches nothing and shows
     * no selection; `isDefaultFilters` stayed false, so the empty state offered "Zrušit
     * filtry" for a filter that was not applied; and `?ownership=ORGANIZATION` went into the
     * URL for a request that carried no such narrowing.
     */
    useEffect(function () {
        if (isAuthorizationLoading || canFilterByOrganization)
            return;
        if (ownershipFilter !== 'ORGANIZATION' && appliedOwnershipFilter !== 'ORGANIZATION')
            return;
        setOwnershipFilter(DEFAULT_PROPOSAL_OWNERSHIP_FILTER);
        setAppliedOwnershipFilter(DEFAULT_PROPOSAL_OWNERSHIP_FILTER);
        writeOwnershipFilter(DEFAULT_PROPOSAL_OWNERSHIP_FILTER);
    }, [isAuthorizationLoading, canFilterByOrganization, ownershipFilter, appliedOwnershipFilter]);
    /**
     * Translate the ownership choice into request params. Returns `null` while the answer is
     * not knowable yet — the identity comes from a second request, and firing the list query
     * before it lands would answer "Jen moje" with everybody's proposals for one render.
     */
    var ownershipParams = useMemo(function () {
        if (appliedOwnershipFilter === 'MINE') {
            if (userId)
                return { createdBy: userId };
            // Same rule as ORGANIZATION below: wait while the identity is still in flight, but
            // once it has arrived without the field we need, fall back to the unnarrowed list.
            // Waiting forever would be a permanent spinner, which is a worse failure than a
            // list that is merely wider than asked — this is a filter, not an access control.
            return isAuthorizationLoading ? null : {};
        }
        if (appliedOwnershipFilter === 'ORGANIZATION') {
            if (userOrganizationId)
                return { organizationId: userOrganizationId };
            // Authorization context loaded and the user has no organization: the stored
            // preference cannot be honoured, so fall back to the unnarrowed list rather than
            // hanging on a request that will never be made.
            return isAuthorizationLoading ? null : {};
        }
        return {};
    }, [appliedOwnershipFilter, userId, userOrganizationId, isAuthorizationLoading]);
    var isDefaultFilters = !appliedQuery && !appliedProposalTypeFilter && !appliedTypeFilter
        && appliedOwnershipFilter === (forcedOwnership !== null && forcedOwnership !== void 0 ? forcedOwnership : DEFAULT_PROPOSAL_OWNERSHIP_FILTER);
    var apiSortBy = SORT_COLUMN_MAP[sortColumn] || 'updatedAt';
    // Derive API params from active tab
    var tabParams = TAB_API_PARAMS[activeTab];
    // Tab-level proposalType overrides the filter dropdown
    var effectiveProposalType = tabParams.proposalType || appliedProposalTypeFilter || undefined;
    /**
     * CZNA-780: only the first page asks the backend to COUNT. Every cursor page reuses the
     * count cached under `resultSetKey`, so the extra `count(*)` is paid once per filter
     * combination rather than once per page.
     *
     * <p>Cursors alone are the test — unlike the records list, this one has no relevance mode
     * and therefore no offset paging: `fetchMoreData` always sets exactly one cursor, and
     * every path that returns to page 1 (`handleSearch`, `handleTabChange`, `handleSort`,
     * both clears) resets `keysetState` to `{}` in the same breath as `pageOffset` to 0.
     */
    var isFirstPage = !keysetState.afterSortValue && !keysetState.beforeSortValue;
    /**
     * CZNA-780: everything the backend's count specification narrows by — the tab (which
     * supplies `status` and may override `proposalType`), the heading query, both type filters
     * and the ownership narrowing. Sort order, limit and cursors are deliberately NOT here:
     * `buildSpecification` ignores them, so a column click must not throw the count away.
     *
     * <p>It is ONE object, spread into the request below and stringified into `resultSetKey`,
     * because the two must not be able to disagree. Two hand-written lists agree only until
     * whoever adds the next filter remembers both places, and the symptom of forgetting is a
     * silently stale count under a filter that changed — the CZNA-533 defect, reintroduced.
     * Add a filter here and both the request and the cache key learn about it at once.
     */
    var appliedFilterParams = useMemo(function () { return (__assign({ status: tabParams.status, heading: appliedQuery || undefined, proposalType: effectiveProposalType, recordType: appliedTypeFilter || undefined }, (ownershipParams !== null && ownershipParams !== void 0 ? ownershipParams : {}))); }, [tabParams.status, appliedQuery, effectiveProposalType, appliedTypeFilter, ownershipParams]);
    /** Identity of the tab + filter combination a cached count was measured for. */
    var resultSetKey = useMemo(function () { return JSON.stringify(appliedFilterParams); }, [appliedFilterParams]);
    var _1 = useQuery([PROPOSALS_QUERY_ROOT, activeTab, appliedQuery, appliedProposalTypeFilter, appliedTypeFilter, ownershipParams, apiSortBy, sortDirection, keysetState], function () { return fetchProposals(ky, __assign(__assign(__assign({}, appliedFilterParams), { sortBy: apiSortBy, sortOrder: toApiSortOrder(sortDirection), limit: PAGE_SIZE, 
        // CZNA-780: without this the backend skips its COUNT query (and, since CZNA-795,
        // omits totalRecords rather than answering the 0 the header once printed as fact).
        includeTotalCount: isFirstPage }), keysetState)); }, {
        keepPreviousData: true,
        // Withhold the request rather than send it unfiltered — see ownershipParams.
        enabled: ownershipParams !== null,
    }), data = _1.data, isLoading = _1.isLoading, isFetching = _1.isFetching, isPreviousData = _1.isPreviousData, error = _1.error, refetch = _1.refetch;
    /**
     * CZNA-595 — the tab badges. One aggregate request covers all seven tabs; the naive
     * shape would be one count per tab on every list render.
     *
     * <p>Keyed and narrowed by `ownershipParams` ALONE, not by the tab, the heading search
     * or the type filters. That is not a shortcut: `handleTabChange` clears the search and
     * both type filters while deliberately keeping the ownership choice, so "what you get
     * when you click that tab" is precisely the count under the current ownership. Keying it
     * on the active tab would also refetch all seven numbers on every tab click for an
     * answer that cannot have changed.
     *
     * <p>The accepted consequence: the in-tab filters narrow the list WITHOUT leaving the
     * tab, so on the ACTIVE tab the badge can read 47 beside three visible rows. The badge
     * lives in the tab rail and answers "how much is in that queue" — the way a mail
     * folder's count does not follow a search inside the folder. Making it follow them would
     * change what the other six badges mean mid-keystroke and refetch all seven on every
     * filter, which is the cost this ticket exists to avoid. Pinned by
     * "does not re-key the counts query when the in-tab type filters are applied".
     *
     * <p>Disabled on the same condition as the list: until the identity behind "Jen moje" /
     * "Moje instituce" has landed, a request would answer for the wrong scope.
     */
    var tabCountRows = useQuery(
    // Nested UNDER the list's root key on purpose. Every proposal mutation invalidates
    // `[PROPOSALS_QUERY_ROOT]` — approve, reject, request-changes, submit and save, eight
    // call sites in useProposalReviewActions / RejectRequestChangesButtons / ProposalDetail /
    // ProposalForm. React Query matches keys element by element, so a sibling root like
    // 'czna-authorities-proposal-counts' would never be hit by any of them: the list would
    // redraw after an approval and the badge would keep the old number for the rest of the
    // session (staleTime only marks data stale, it does not refetch it). Sharing the root
    // means the ninth call site gets this for free instead of having to know about badges.
    [PROPOSALS_QUERY_ROOT, 'counts', ownershipParams], function () { return fetchProposalTabCounts(ky, ownershipParams !== null && ownershipParams !== void 0 ? ownershipParams : {}); }, {
        enabled: ownershipParams !== null,
        keepPreviousData: true,
        // The ticket allows a badge to lag by tens of seconds; it must not be wrong by an
        // order of magnitude. This keeps a tab click from refetching counts that a moment
        // ago were correct, without letting them go stale across a work session.
        staleTime: 30000,
    }).data;
    /**
     * Counts are best-effort decoration: a failed or in-flight aggregate renders as no
     * badges, never as zeros. A zero is a claim ("you have none here"); absence is not.
     */
    var tabCounts = useMemo(
    // Array.isArray, not a truthiness check: the badges are decoration on a page whose
    // real job is the list, so an unexpected payload has to degrade to "no badges" rather
    // than throw during render and take the whole list down with it.
    function () { return (Array.isArray(tabCountRows) ? foldProposalCountsByTab(tabCountRows) : null); }, [tabCountRows]);
    /**
     * CZNA-780: latch the count together with the result set it was measured for.
     *
     * <p>`isPreviousData` is the guard that matters: `keepPreviousData` keeps the OLD
     * response visible while the new one is in flight, so without it the first render after
     * a tab click would file the previous tab's number under the new tab's key.
     */
    useEffect(function () {
        if (!data || isPreviousData || !isFirstPage)
            return;
        // CZNA-795: absent means the backend did not count. Never fold that into 0 — a first
        // page always asks, so this only guards against a response that ignored the flag.
        if (data.totalRecords == null) {
            setCountForResultSet(null);
            return;
        }
        var exact = data.totalRecords;
        // Return the SAME object when nothing changed. `data` is a fresh identity on every
        // response — including a refetch that re-measured the same number — and a state update
        // that only ever writes a new object would re-run this effect on its own render, which
        // is a loop and not a hypothetical one: it exhausted the jest heap the first time.
        setCountForResultSet(function (prev) { return (prev && prev.key === resultSetKey && prev.exact === exact
            ? prev
            : { key: resultSetKey, exact: exact }); });
    }, [data, isPreviousData, isFirstPage, resultSetKey]);
    /**
     * CZNA-795 — a proposal mutation makes the latched count stale, and on a cursor page
     * nothing would ever re-measure it: all eight mutations invalidate
     * `[PROPOSALS_QUERY_ROOT]`, the refetch of page 2+ is not asked to count, and the header
     * used to keep the pre-approval number until the user went back to page 1.
     *
     * <p>So an invalidation under the root drops the latch — the header then omits the number
     * rather than assert a stale one. Listening for the invalidation itself, not for "a
     * refetch of the same page", is deliberate: revisiting a cached page (1 → 2 → 1 → 2)
     * background-refetches under the same key too, and must not blank the header. On page 1
     * the latch is left alone because that refetch carries `includeTotalCount` and replaces
     * the number itself; blanking it first would only flicker the header.
     */
    var queryClient = useQueryClient();
    var isFirstPageRef = useRef(isFirstPage);
    isFirstPageRef.current = isFirstPage;
    useEffect(function () { return queryClient.getQueryCache().subscribe(function (event) {
        var _a;
        if ((event === null || event === void 0 ? void 0 : event.type) !== 'queryUpdated' || ((_a = event.action) === null || _a === void 0 ? void 0 : _a.type) !== 'invalidate')
            return;
        if (event.query.queryKey[0] !== PROPOSALS_QUERY_ROOT)
            return;
        if (isFirstPageRef.current)
            return;
        setCountForResultSet(null);
    }); }, [queryClient]);
    // Read the remembered count back only while it still describes the current tab + filters.
    var currentCount = (countForResultSet === null || countForResultSet === void 0 ? void 0 : countForResultSet.key) === resultSetKey ? countForResultSet : null;
    /**
     * CZNA-780 — the count shown to the **user**. `null` means we do not know it yet (the
     * first-page request is still in flight, or the identity behind an ownership filter has
     * not landed). `SearchLayout` omits the subtitle on null; there is deliberately no number
     * to fall back on, because 0 is a claim and "not measured" is not.
     *
     * <p>`??`, never `||`: a MEASURED zero is an answer — an empty queue must read "Nalezeno 0
     * záznamů" — and `||` would fold it back into "unknown". That is the same conflation of 0
     * with absence, one pipe further along, that this ticket exists to remove. Pinned by
     * "reports a counted zero as zero, not as unknown".
     */
    var totalRecords = (_d = currentCount === null || currentCount === void 0 ? void 0 : currentCount.exact) !== null && _d !== void 0 ? _d : null;
    /**
     * CZNA-780 — what drives **MCL's prev/next paging**, a different question from the one
     * above and never rendered as a record count. `MCLRenderer.getCanGoNext` treats a falsy
     * `totalCount` as "unknown" and probes `contentData` for a hole, so "next" is answered
     * explicitly and follows `hasNextPage` while the real total is unknown. CZNA-1191 — the
     * count is in page coordinates, not the corpus total; why is in `mclPaging`.
     */
    var _2 = useMemo(function () {
        var _a;
        return mclPaging({
            totalRecords: totalRecords,
            hasNextPage: data === null || data === void 0 ? void 0 : data.hasNextPage,
            pageOffset: pageOffset,
            itemCount: ((_a = data === null || data === void 0 ? void 0 : data.items) === null || _a === void 0 ? void 0 : _a.length) || 0,
        });
    }, [totalRecords, data === null || data === void 0 ? void 0 : data.hasNextPage, (_f = data === null || data === void 0 ? void 0 : data.items) === null || _f === void 0 ? void 0 : _f.length, pageOffset]), paginationTotalCount = _2.totalCount, paginationCanGoNext = _2.canGoNext;
    var handleClearSearch = useCallback(function () {
        setQuery('');
        setAppliedQuery('');
        setPageOffset(0);
        setKeysetState({});
    }, []);
    var handleClearFilters = useCallback(function () {
        setQuery('');
        setAppliedQuery('');
        setProposalTypeFilter(null);
        setAppliedProposalTypeFilter(null);
        setTypeFilter(null);
        setAppliedTypeFilter(null);
        // CZNA-593: "Zrušit filtry" is the only path that clears the remembered ownership
        // choice. It is offered from the no-results empty state, so it has to be able to
        // reach the unnarrowed list — otherwise a remembered "Jen moje" leaves the user with
        // a Clear button that does not clear.
        var clearedOwnership = forcedOwnership !== null && forcedOwnership !== void 0 ? forcedOwnership : DEFAULT_PROPOSAL_OWNERSHIP_FILTER;
        setOwnershipFilter(clearedOwnership);
        setAppliedOwnershipFilter(clearedOwnership);
        if (!forcedOwnership)
            writeOwnershipFilter(DEFAULT_PROPOSAL_OWNERSHIP_FILTER);
        setPageOffset(0);
        setKeysetState({});
    }, [forcedOwnership]);
    var handleProposalTypeFilterChange = useCallback(function (value) {
        setProposalTypeFilter(value);
    }, []);
    var handleTypeFilterChange = useCallback(function (value) {
        setTypeFilter(value);
    }, []);
    var handleOwnershipFilterChange = useCallback(function (value) {
        ownershipTouched.current = true;
        setOwnershipFilter(value);
    }, []);
    var handleSort = useCallback(function (_e, _a) {
        var name = _a.name;
        if (name === sortColumn) {
            setSortDirection(function (prev) { return (prev === 'ascending' ? 'descending' : 'ascending'); });
        }
        else {
            setSortColumn(name);
            setSortDirection('ascending');
        }
        setPageOffset(0);
        setKeysetState({});
    }, [sortColumn]);
    var toggleFilterPane = useCallback(function () {
        setFilterPaneIsVisible(function (prev) { return !prev; });
    }, []);
    var fetchMoreData = useCallback(function (offset) {
        var items = data === null || data === void 0 ? void 0 : data.items;
        if (!(items === null || items === void 0 ? void 0 : items.length))
            return;
        if (offset > pageOffset) {
            // Next page — use last item's sort value and id as cursor
            var lastItem = items[items.length - 1];
            setKeysetState({
                afterSortValue: getSortValue(lastItem, apiSortBy),
                afterId: lastItem.id,
            });
        }
        else {
            // Previous page — use first item's sort value and id as cursor
            var firstItem = items[0];
            setKeysetState({
                beforeSortValue: getSortValue(firstItem, apiSortBy),
                beforeId: firstItem.id,
            });
        }
        setPageOffset(offset);
    }, [pageOffset, data === null || data === void 0 ? void 0 : data.items, apiSortBy]);
    return {
        activeTab: activeTab,
        handleTabChange: handleTabChange,
        query: query,
        setQuery: setQuery,
        pageOffset: pageOffset,
        pageSize: PAGE_SIZE,
        proposalTypeFilter: proposalTypeFilter,
        handleProposalTypeFilterChange: handleProposalTypeFilterChange,
        typeFilter: typeFilter,
        handleTypeFilterChange: handleTypeFilterChange,
        ownershipFilter: ownershipFilter,
        handleOwnershipFilterChange: handleOwnershipFilterChange,
        canFilterByOrganization: canFilterByOrganization,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        handleSort: handleSort,
        filterPaneIsVisible: filterPaneIsVisible,
        toggleFilterPane: toggleFilterPane,
        handleSearch: handleSearch,
        handleClearSearch: handleClearSearch,
        handleClearFilters: handleClearFilters,
        isDefaultFilters: isDefaultFilters,
        fetchMoreData: fetchMoreData,
        items: (data === null || data === void 0 ? void 0 : data.items) || [],
        // CZNA-780: two counts, deliberately not one. `totalRecords` is what the user is told
        // (null = not known); `paginationTotalCount` only feeds MCL's paging arithmetic.
        totalRecords: totalRecords,
        paginationTotalCount: paginationTotalCount,
        paginationCanGoNext: paginationCanGoNext,
        // CZNA-595 — null while unknown (loading or failed); never a map of zeros.
        tabCounts: tabCounts,
        // CZNA-593: while the ownership filter is waiting on the user's identity the query is
        // disabled, which react-query v3 reports as `idle` (isLoading === false), not as
        // loading. Left as-is the list would render its "nothing found" empty state for a beat
        // before the real request even starts.
        isLoading: isLoading || ownershipParams === null,
        isFetching: isFetching,
        error: error,
        // CZNA-538: the lists render a real error state now, and an error state without
        // a way out is a dead end — the retry button calls this.
        refetch: refetch,
    };
};
export default useProposalList;
